import { useState } from "react";
import { FileText, Upload, History, GripVertical } from "lucide-react";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { Button } from "../ui/button";
import { Switch } from "../ui/switch";
import { Tooltip, TooltipContent, TooltipTrigger } from "../ui/tooltip";
import { HistorialEdicionModal } from "../HistorialEdicionModal";

interface PruebaItem {
  nombre: string;
  ROW_ID: string;
  nomId?: string;
  ACEPTADO: string;
  DOC?: string;
  RAZON_RECHAZO?: string;
  EDITADO?: boolean;
  EDITADO_POR?: string;
  folioEmpresa?: number;
  EN_BORRADOR?: boolean;
  MOSTRAR_OBSERVACIONES?: boolean;
  INCLUIR_REPORTE_CLIENTE?: boolean;
}

interface PedidoPruebaItemProps {
  prueba: PruebaItem;
  pruebaIndex: number;
  dragId: string;
  dragDisabled?: boolean;
  onLlenar: (pruebaNombre: string, pruebaIndex: number) => void;
  onRevisar: (pruebaNombre: string, rowId: string) => void;
  onEditar?: (pruebaNombre: string, rowId: string) => void;
  onContinuar?: (pruebaNombre: string, rowId: string) => void;
  onUpload: (rowId: string, pruebaNombre: string, nomId?: string) => void;
  onToggleReporteCliente?: (nomId: string, incluir: boolean) => void;
  modoEdicionAdmin?: boolean;
  puedeCapturar?: boolean;
  pedidoCerrado?: boolean;
}

export function PedidoPruebaItem({ prueba, pruebaIndex, dragId, dragDisabled = false, onLlenar, onRevisar, onEditar, onContinuar, onUpload, onToggleReporteCliente, modoEdicionAdmin, puedeCapturar = true, pedidoCerrado = false }: PedidoPruebaItemProps) {
  const pruebaKey = `${prueba.nombre}__${prueba.ROW_ID}`;
  const [showHistorial, setShowHistorial] = useState(false);
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: dragId, disabled: dragDisabled });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <li key={pruebaKey} ref={setNodeRef} style={style} className="p-3 border rounded flex justify-between items-center bg-background">
      <div className="flex items-center gap-2">
        {!dragDisabled && (
          <button
            type="button"
            {...attributes}
            {...listeners}
            title="Arrastrar para reordenar"
            className="cursor-grab active:cursor-grabbing text-muted-foreground touch-none shrink-0"
          >
            <GripVertical className="h-5 w-5" />
          </button>
        )}
        <div>
          <span className="font-semibold block">{prueba.nombre || 'Sin nombre'}</span>
          <span className="block text-xs text-muted-foreground mt-1">Folio: {prueba.folioEmpresa ?? (prueba.ROW_ID || 'Sin ID')}</span>
        </div>
      </div>
      <div className="flex items-center gap-3 flex-wrap justify-end">
        {/* Tag de estado */}
        {['SI', 'aceptado', 'ACEPTADO', 'Aceptado'].includes(prueba.ACEPTADO) ? (
          <span className="px-2 py-1 rounded text-xs font-semibold bg-green-100 text-green-700 border border-green-200">ACEPTADO</span>
        ) : ['NO', 'rechazado', 'RECHAZADO', 'Rechazado'].includes(prueba.ACEPTADO) ? (
          <>
            <span className="px-2 py-1 rounded text-xs font-semibold bg-red-100 text-red-700 border border-red-200">RECHAZADO</span>
            {prueba.RAZON_RECHAZO && (
              <Tooltip>
                <TooltipTrigger>
                  <span className="ml-1 cursor-pointer inline-flex items-center">
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <circle cx="8" cy="8" r="7" stroke="#dc2626" strokeWidth="2" fill="#fff" />
                      <text x="8" y="11" textAnchor="middle" fontSize="10" fill="#dc2626" fontFamily="Arial">i</text>
                    </svg>
                  </span>
                </TooltipTrigger>
                <TooltipContent>{prueba.RAZON_RECHAZO}</TooltipContent>
              </Tooltip>
            )}
          </>
        ) : prueba.EN_BORRADOR ? (
          <span className="px-2 py-1 rounded text-xs font-semibold bg-amber-100 text-amber-700 border border-amber-200">BORRADOR</span>
        ) : (
          <span className="px-2 py-1 rounded text-xs font-semibold bg-gray-100 text-gray-600 border border-gray-200">SIN REVISIÓN</span>
        )}

        {/* Toggle: incluir esta prueba en el Reporte Cliente (la Bitácora siempre incluye todas) */}
        {prueba.ROW_ID && prueba.nomId && (
          <div className="flex items-center gap-1.5" title="Incluir esta prueba en el Reporte Cliente">
            <Switch
              checked={prueba.INCLUIR_REPORTE_CLIENTE !== false}
              disabled={pedidoCerrado}
              onCheckedChange={(checked) => onToggleReporteCliente?.(prueba.nomId!, checked)}
            />
            <span className="text-xs text-muted-foreground">Reporte cliente</span>
          </div>
        )}

        {/* Botón historial de ediciones */}
        {prueba.EDITADO && (
          <>
            <Button
              size="icon"
              variant="ghost"
              title="Ver historial de ediciones"
              className="h-7 w-7 text-orange-600 hover:text-orange-800 hover:bg-orange-50"
              onClick={e => { e.stopPropagation(); setShowHistorial(true); }}
            >
              <History className="w-4 h-4" />
            </Button>
            {showHistorial && (
              <HistorialEdicionModal
                open={showHistorial}
                onClose={() => setShowHistorial(false)}
                nomId={prueba.nomId || ''}
                pruebaNombre={prueba.nombre}
              />
            )}
          </>
        )}

        {/* Botón: LLENAR si no hay ROW_ID; CONTINUAR si es borrador; REVISAR si ya está guardada */}
        {prueba.ROW_ID ? (
          <>
            <Button
              size="sm"
              variant={prueba.EN_BORRADOR ? "outline" : "default"}
              onClick={(e) => {
                e.stopPropagation();
                if (prueba.EN_BORRADOR) onContinuar?.(prueba.nombre, prueba.ROW_ID);
                else onRevisar(prueba.nombre, prueba.ROW_ID);
              }}
            >
              {prueba.EN_BORRADOR ? 'CONTINUAR' : 'REVISAR'}
            </Button>
            {modoEdicionAdmin && (
              <Button
                size="sm"
                variant="outline"
                className="border-amber-500 text-amber-700 hover:bg-amber-50"
                onClick={(e) => { e.stopPropagation(); onEditar?.(prueba.nombre, prueba.ROW_ID); }}
              >
                EDITAR
              </Button>
            )}
            {(!prueba.DOC || prueba.DOC === '') && (
              <Button
                variant="ghost"
                size="icon"
                title="Subir documento"
                onClick={e => { e.stopPropagation(); onUpload(prueba.ROW_ID, prueba.nombre, prueba.nomId); }}
              >
                <Upload className="w-5 h-5" />
              </Button>
            )}
            {prueba.DOC && prueba.DOC !== '' && (
              <a
                href={prueba.DOC}
                target="_blank"
                rel="noopener noreferrer"
                title="Ver documento adjunto"
                className="inline-flex items-center justify-center text-blue-600 hover:text-blue-800"
              >
                <FileText className="h-5 w-5" />
              </a>
            )}
          </>
        ) : puedeCapturar ? (
          <Button
            size="sm"
            variant="outline"
            onClick={(e) => { e.stopPropagation(); onLlenar(prueba.nombre, pruebaIndex); }}
          >
            LLENAR
          </Button>
        ) : null}
      </div>
    </li>
  );
}
